import * as React from 'react';
import isLocalStorageAvailable from '../../../services/mini/isLocalStorageAvailable';

type ResizerProps = {
    x: number
    y: number
    divider: string
    lsKey: string
    styleName: string
    max: number
    min: number
    isOpened?: boolean
    disabled?: boolean
    onStateChange: (state: ResizerState) => void;
    onResizeStart: () => void;
    onResizeEnd: ({ height, width }: {
        height: number
        width: number
    }) => boolean;
};

type ResizerState = {
    x: number
    y: number
    inMotion: boolean
    ignoreLs: boolean
    isOpened: boolean
    defaultX: number
    defaultY: number
    isInit: boolean
};

export class Resizer extends React.Component<ResizerProps, ResizerState>{

    divider: React.RefObject<HTMLDivElement>;
    parentEl: React.RefObject<HTMLDivElement>;
    isDown: boolean;
    initWidth: number;
    initHeight: number;
    initialPosition: { x: 0, y: 0 };

    constructor(props) {
        super(props);
        this.state = {
            x: null,
            y: null,
            inMotion: false,
            ignoreLs: false,
            isOpened: null,
            defaultX: null,
            defaultY: null,
            isInit: false,
        };

        this.isDown = false;
        this.divider = React.createRef();
        this.parentEl = React.createRef();
        this.initialPosition = { x: 0, y: 0 };
    }

    componentDidMount() {
        const ls = this.getLocalStorageData();
        const sourceData = ls ? ls : this.props;
        const { isOpened } = sourceData;
        let { x, y, } = sourceData;
        let isOpenedVal = this.getOpenedState();

        if (isOpenedVal === null) {
            isOpenedVal = isOpened || this.props.isOpened || false;
        }

        document.addEventListener('mousemove', this.onMouseMove, true);
        document.addEventListener('mouseup', this.onMouseUp, true);

        x = isFinite(x) ? x : this.isVertical() ? 0 : 100;
        y = isFinite(y) ? y : this.isVertical() ? 100 : 0;

        const defaultX = isFinite(x) ? x : (this.isVertical() ? x : 100);
        const defaultY = isFinite(y) ? y : (this.isVertical() ? 100 : y);

        x = this.isVertical() ? (isOpenedVal ? x : 0) : 100;
        y = this.isVertical() ? 100 : (isOpenedVal ? y : 0);

        if (isFinite(x) || isFinite(y)) {
            const state = {
                defaultX,
                defaultY,
                x,
                y,
                isOpened: isOpenedVal,
                ignoreLs: true,
            };

            this.setState(state, () => {
                if (this.props.onStateChange) {
                    this.props.onStateChange({ ...this.state });
                }
                this.setState({ isInit: true });
            });
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousemove', this.onMouseMove, true);
        document.removeEventListener('mouseup', this.onMouseUp, true);
    }

    componentDidUpdate(prevProps, prevState) {
        let {
            max,
            min,
        } = this.props;
        const {
            divider,
        } = this.props;
        let {
            x,
            y,
        } = this.state;

        const {
            inMotion,
        } = this.state;

        const xChanged = x !== prevState.x;
        const yChanged = y !== prevState.y;

        max = isFinite(max) ? max : Infinity;

        min = min || 0;

        const child = this.getChild();
        if (!child) return;

        if (prevProps.divider !== divider) {

            if (divider) {
                if (this.isVertical()) {
                    child.style.width = `${x}%`;
                } else {
                    child.style.height = `${y}%`;
                }
            } else {
                child.style.width = '';
                child.style.height = '';
            }
        }

        if (yChanged || xChanged) {
            if (divider) {
                if (!inMotion) {
                    x = Math.min(Math.max(x, min), max);
                    y = Math.min(Math.max(y, min), max);
                }

                if (this.isVertical()) {
                    child.style.width = `${x}%`;
                } else {
                    child.style.height = `${y}%`;
                }
            }
        }
    }

    onMouseDown = (e) => {
        if (this.props.disabled) {
            return;
        }
        document.body.style.cursor = `${this.isVertical() ? 'col' : 'row'}-resize`;
        document.body.style.userSelect = 'none';

        this.isDown = true;
        this.initialPosition = this.getMousePosition(e);

        const domNode = this.getChild();
        if (this.isVertical()) {
            this.initWidth = domNode.style.width ? parseInt(domNode.style.width) : domNode.offsetWidth / (domNode.parentNode as HTMLDivElement).offsetWidth;
        } else {
            this.initHeight = domNode.style.height ? parseInt(domNode.style.height) : domNode.offsetHeight / (domNode.parentNode as HTMLDivElement).offsetHeight;
        }
        if (this.props.onResizeStart) {
            this.props.onResizeStart();
        }
    };

    onMouseMove = (e) => {
        if (this.isDown) {
            const { divider, max, min } = this.props;
            const { x, y } = this.initialPosition;
            const { x: curX, y: curY } = this.getMousePosition(e);
            const parentEl = this.getChild().parentNode as HTMLDivElement;

            if (this.isVertical()) {
                const delta = ((curX - x) / parentEl.offsetWidth) * 100;
                const newX = Math.max(Math.min(divider === 'right' ? this.initWidth + delta : this.initWidth - delta, max), min);
                this.setState({
                    x: newX,
                    ignoreLs: true,
                });
            } else {
                const delta = ((curY - y) / parentEl.offsetHeight) * 100;
                const newY = divider === 'top' ? this.initHeight - delta : this.initHeight + delta;
                this.setState({
                    y: newY,
                    ignoreLs: true,
                });
            }
        }
    };

    onMouseUp = () => {
        if (!this.props.divider || !this.isDown) {
            return;
        }
        this.isDown = false;
        document.body.style.cursor = 'default';
        document.body.style.userSelect = '';
        if (this.props.onResizeEnd) {
            const resizedChild = this.getChild();
            if (resizedChild) {
                const shouldSaveToLs = this.props.onResizeEnd({
                    height: resizedChild.clientHeight,
                    width: resizedChild.clientWidth,
                });

                if (shouldSaveToLs) {
                    if (!this.state.isOpened) {
                        this.setState({ isOpened: true }, () => {
                            if (this.props.onStateChange) {
                                this.props.onStateChange({ ...this.state });
                            }
                            this.saveLs();
                        });
                    } else {
                        this.saveLs();
                    }
                }
            }
        } else {
            this.saveLs();
        }
        if (this.props.onStateChange) {
            this.props.onStateChange({ ...this.state });
        }
    };

    saveLs() {
        const { max } = this.props;
        const { y, x } = this.state;

        const newDefaultY = (max && !this.isVertical()) ? (y > max ? max : y) : y;
        const newDefaultX = (max && this.isVertical()) ? (x > max ? max : x) : x;

        this.setState({
            defaultX: newDefaultX,
            defaultY: newDefaultY,
        }, () => {
            this.setLocalStorageData();
        });
    }

    isVertical() {
        const { divider } = this.props;
        return divider === 'right' || divider === 'left';
    }

    getChild(): HTMLDivElement {
        return this.parentEl.current;
    }

    getMousePosition(e) {
        return {
            x: e.clientX,
            y: e.clientY
        };
    }

    animateResize(x = 0, y = 0, cb, isOpened, ignoreLs = false) {
        this.setState({
            inMotion: true,
            isOpened: true,
        }, () => {
            this.setState({
                x,
                y,
                ignoreLs,
            }, () => {
                setTimeout(() => {
                    this.setState({
                        inMotion: false,
                        isOpened,
                    }, () => {
                        if (this.props.onStateChange) {
                            this.props.onStateChange({ ...this.state });
                        }
                        if (cb) {
                            cb();
                        }
                    });
                }, 300);
            });
        });
    }

    collapse(cb: () => void | null) {
        const x = this.isVertical() ? 0 : 100;
        const y = this.isVertical() ? 100 : 0;
        this.animateResize(x, y, cb, false);
        this.setOpenedState(false);
    }

    open(cb: () => void | null) {
        const { defaultX, defaultY } = this.state;
        this.animateResize(defaultX, defaultY, cb, true);
        this.setOpenedState(true);
    }

    toggle(cb: () => void | null) {
        if (this.state.isOpened) {
            this.collapse(cb);
        } else {
            this.open(cb);
        }
    }

    setOpenedState(isOpened) {
        if (this.props.lsKey && isLocalStorageAvailable()) {
            localStorage.setItem(`${this.props.lsKey}.opened`, JSON.stringify({
                isOpened,
            }));
        }
    }

    getOpenedState() {
        if (this.props.lsKey && isLocalStorageAvailable()) {
            try {
                return JSON.parse(localStorage.getItem(`${this.props.lsKey}.opened`)).isOpened;
            } catch (e) { }
        }
        return null;
    }

    setLocalStorageData() {
        if (this.props.lsKey && isLocalStorageAvailable()) {
            localStorage.setItem(this.props.lsKey, JSON.stringify({
                x: this.state.defaultX,
                y: this.state.defaultY,
            }));
        }
    }

    getLocalStorageData() {
        if (this.props.lsKey && isLocalStorageAvailable()) {
            try {
                return JSON.parse(localStorage.getItem(this.props.lsKey));
            } catch (e) { }
        }
        return null;
    }

    render() {
        const { children, divider, styleName } = this.props;
        const dividerEl = <div
            ref={this.divider}
            className={`resizer_divider ${divider}`}
            onMouseDown={this.onMouseDown} />;

        return (
            <div className={`resizer ${divider} ${styleName} ${(this.state.inMotion ? 'resizer_in-motion' : '')} ${!this.state.isInit && 'resizer_hidden'}`}
                ref={this.parentEl}>
                {(divider === 'left' || divider === 'top') && dividerEl}
                {children}
                {divider === 'right' && dividerEl}
            </div>
        );
    }
}
